import { useTranslation } from 'react-i18next';

import {
	Box,
	DotsHorizontalOutline,
	IconButton,
	PencilAltOutline,
	Popover,
	PopoverContent,
	PopoverTrigger,
	rem,
	TrashOutline,
	useDisclosure,
} from '@bonusly/design-system';
import { useIdentity } from '@bonusly/who-am-i';

import { Bonus } from '../../../../../types/bonus';
import { useRemoveCommentFromQueriesData } from '../../cache/mutators/comments';
import { useDeleteBonus } from '../../components/bonus/hooks/use-delete-bonus';
import { IconOption } from '../../components/common/dropdown/icon-option';
import { ConfirmationModal } from '../../components/common/modal';
import { BonusFeedTrackedAction, useTrackBonusFeedItemEvent } from '../../hooks/use-bonus-feed-item-analytics';
import { useToast } from '../../hooks/use-toast';

export interface CommentMenuProps {
	bonus: Bonus;
	onEdit: VoidFunction;
}

export const useDeleteComment = (onClose: VoidFunction) => {
	const { t } = useTranslation('recognition');
	const { createSuccessToast } = useToast();
	const updateQueries = useRemoveCommentFromQueriesData();
	const { refetchProfile } = useIdentity();

	return useDeleteBonus({
		onSuccess: (_data, { id }) => {
			updateQueries(id);
			refetchProfile();

			createSuccessToast(t('delete_comment.feedback.success'));
			onClose();
		},
	});
};

export const CommentMenu = ({ bonus, onEdit }: CommentMenuProps) => {
	const { t } = useTranslation(['recognition', 'common']);
	const confirmDeleteModal = useDisclosure();

	const trackDeleteEvent = useTrackBonusFeedItemEvent(bonus, BonusFeedTrackedAction.CommentDelete);

	const deleteBonus = useDeleteComment(() => {
		confirmDeleteModal.onClose();
		trackDeleteEvent();
	});

	return (
		<>
			<Popover isLazy placement="bottom-start">
				{({ onClose }) => (
					<>
						<PopoverTrigger>
							<IconButton
								_hover={{ backgroundColor: 'gray.50' }}
								aria-label={t('comment.menu_button_label', {
									ns: 'recognition',
								})}
								backgroundColor="bg.surface"
								borderRadius="full"
								color="gray.600"
								icon={<DotsHorizontalOutline size="sm" />}
								w={6}
								minWidth={6}
								style={{ height: rem(24) }}
							/>
						</PopoverTrigger>

						<PopoverContent boxShadow="xl" minW={rem(200)} width="auto" border="none">
							<Box as="ul" role="listbox" aria-label="Select action" p={1}>
								<IconOption
									icon={<PencilAltOutline />}
									onClick={() => {
										onEdit();
										onClose();
									}}
									text={t('edit', { ns: 'common' })}
								/>
								<IconOption
									icon={<TrashOutline />}
									text={t('delete', { ns: 'common' })}
									onClick={() => {
										confirmDeleteModal.onOpen();
										onClose();
									}}
								/>
							</Box>
						</PopoverContent>
					</>
				)}
			</Popover>

			<ConfirmationModal
				confirmColorScheme="danger"
				confirmText={t('comment.confirm_delete.text', { ns: 'recognition' })}
				description={t('comment.confirm_delete.description', {
					ns: 'recognition',
				})}
				header={t('comment.confirm_delete.header', { ns: 'recognition' })}
				isLoading={deleteBonus.isLoading}
				onConfirm={() => {
					deleteBonus.mutate({ id: bonus.id });
				}}
				{...confirmDeleteModal}
			/>
		</>
	);
};
